import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import {
  focusWithSetTabIndex,
  getOpener,
} from "../../../../../../common/utils/focus.ts";
import { createPortal } from "react-dom";
import { useWatchForExternalPanelClose } from "./panelClose.ts";
import { OpenerScrollMode } from "./panelTypes.ts";

export interface PanelControllerOpts {
  scrollMode?: OpenerScrollMode;
}

// Used with sticky top elements to make scroll-tos actually
// bring the relevant content into view
export const scrollOffsetElementId = "default-template-panel-scroll-offset";

export default function usePanelController(
  onRequestClose: () => void,
  portalTargetId: string,
  opts: PanelControllerOpts = {}
) {
  const openerRef = useRef<HTMLElement>();
  const panelRef = useRef<HTMLDivElement>(null);
  useWatchForExternalPanelClose(onRequestClose);

  /**
   * Scrolling to the opener button doesn't necessarily look great; scroll
   * to a better context element (e.g. book card) instead
   */
  const prettyScrollToOpener = useCallback(() => {
    if (opts.scrollMode === "none") {
      return;
    }

    const openerElement = openerRef.current?.closest("li") ?? openerRef.current;
    if (openerElement) {
      const offset = parseInt(
        getComputedStyle(openerElement).getPropertyValue("--home-inset"),
        10
      );
      const rect = openerElement.getBoundingClientRect();

      const extraOffset =
        document.getElementById(scrollOffsetElementId)?.getBoundingClientRect()
          .height ?? 0;

      window.scroll(0, window.scrollY + rect.top - offset - extraOffset);
    }
  }, [opts]);

  const getPortalTarget = useCallback(() => {
    return document.getElementById(portalTargetId) ?? document.body;
  }, [portalTargetId]);

  /**
   * Close the panel when a new one is opened.
   */
  useEffect(() => {
    const targetNode = getPortalTarget();
    if (!targetNode) {
      return;
    }

    const callback = function (mutationsList: MutationRecord[]) {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          if (
            // Auto-close if a new panel was opened
            mutation.addedNodes.length > 0 &&
            // Though depending on the target there might be unrelated mutations
            // to ignore, like the sidebar reappearing. Panels go at the end so
            // should have no next sibling
            mutation.nextSibling === null &&
            // But not if this panel switched from full screen to inline
            targetNode.lastChild !== panelRef.current
          ) {
            onRequestClose();
          }
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, {
      childList: true,
    });
    return () => observer.disconnect();
  }, [onRequestClose, getPortalTarget]);

  /**
   * Focus the panel when it's opened.
   */
  useLayoutEffect(() => {
    if (panelRef.current) {
      const opener = getOpener();
      // This check is due to an unfortunate interaction between strict mode
      // and the opener being set to display none on smaller screens. You'd
      // ordinarily expect the focus cycle to run twice, but the CSS that
      // applies display none to the opener interferes with that because a hidden
      // element can't be focused. On the second pass document.activeElement is
      // the panel itself, which was focused on the first pass.
      if (opener !== panelRef.current) {
        openerRef.current = opener;
        focusWithSetTabIndex(panelRef.current);
        prettyScrollToOpener();
      }
    }
  }, [prettyScrollToOpener]);

  /**
   * Focus the opener when the panel is closed.
   */
  useEffect(() => {
    return () => {
      // Did the panel close without something else (such as another panel)
      // gaining focus? If so, move the focus to the opener and scroll it into
      // view. Needs to support both observer-based force closures and external
      // state-based closures.
      window.setTimeout(() => {
        if (
          !document.activeElement ||
          document.activeElement === document.body
        ) {
          openerRef.current?.focus();
          prettyScrollToOpener();
        }
      }, 10); // Little extra, just in case
    };
  }, [prettyScrollToOpener]);

  /**
   * Render the panel in the portal target.
   */
  const renderPanel = useCallback(
    (content: ReactNode) => {
      return createPortal(content, getPortalTarget());
    },
    [getPortalTarget]
  );

  return useMemo(() => ({ renderPanel, panelRef }), [renderPanel, panelRef]);
}
